import React, { useMemo, useRef, useState } from 'react'
import { Button, CircularProgress, Menu, MenuItem, Tooltip } from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser'
import CheckIcon from '@material-ui/icons/Check'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import CreditCost from 'components/shared/CreditCost'
import strings from 'strings'
import { buildBusinessReport, dbtTone, formatMoney } from './businessReportModel'
import styles from './BusinessDetailReport.module.scss'
import { withReportErrorBoundary } from './ReportErrorBoundary'

const bgvString = strings.APPLICANT.BGV
const PREVIEW_ROWS = 5
const AGING_STYLES = ['agingOnTime', 'agingLate', 'agingLater', 'agingLater', 'agingLatest']
const SEARCH_FROM = 20

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`
const DEROGATORY_HINT = 'Lexis reports derogatory records (such as liens, judgments or criminal records) for this person. Run an owner background check for details.'
const PROFILE_DETAILS_HINT = 'Everything else Lexis, D&B and Experian sent about the company that the sections above don\'t show. Sensitive data, such as SSNs and tax ids, is left out.'
const cx = (...names) => names.filter(Boolean).join(' ')

export const Badge = ({ tone, dot, children }) => (
    <span className={cx(styles.badge, tone && styles[tone], dot && styles.dot)}>{children}</span>
)

const ListTable = ({ columns, rows, rowKey = (row, i) => i }) => {
    const [showAll, setShowAll] = useState(false)
    const [query, setQuery] = useState('')
    const searchable = showAll && rows.length > SEARCH_FROM
    const filtered = searchable && query
        ? rows.filter(row => columns.some(c => String(c.text ? c.text(row) : '').toLowerCase().includes(query.toLowerCase())))
        : rows
    const visible = showAll ? filtered : rows.slice(0, PREVIEW_ROWS)

    return (
        <>
            {searchable && (
                <input
                    className={styles.search}
                    type='search'
                    placeholder='Search'
                    aria-label='Search this list'
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                />
            )}
            <div className={styles.tableWrap}>
                <table className={styles.table}>
                    <thead>
                        <tr>{columns.map(c => <th key={c.label}>{c.label}</th>)}</tr>
                    </thead>
                    <tbody>
                        {visible.map((row, i) => (
                            <tr key={rowKey(row, i)}>
                                {columns.map(c => <td key={c.label} className={c.mono ? styles.mono : undefined}>{c.render(row)}</td>)}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {rows.length > PREVIEW_ROWS && (
                <div className={styles.listActions}>
                    <button type='button' className={styles.textButton} onClick={() => { setShowAll(v => !v); setQuery('') }}>
                        {showAll ? 'Show fewer' : `Show all (${rows.length})`}
                    </button>
                </div>
            )}
        </>
    )
}

export const Section = ({ id, title, summary, open, onToggle, children }) => (
    <section id={id} className={styles.card}>
        <button type='button' className={styles.cardHeader} onClick={onToggle} aria-expanded={open}>
            <ExpandMoreIcon className={cx(styles.chevron, !open && styles.chevronClosed)} />
            <h3 className={styles.cardTitle}>{title}</h3>
            {summary}
        </button>
        {open && <div className={styles.cardBody}>{children}</div>}
    </section>
)

const UccRow = ({ ucc, ended }) => {
    const [open, setOpen] = useState(false)
    const [lender, ...coLenders] = ucc.secured
    const claimsReceivables = ucc.collateral.allAssets || ucc.collateral.receivables
    const facts = [ucc.dateLabel && `Filed ${ucc.dateLabel}`, ended ? ucc.change : ucc.expires && `Expires ${ucc.expires}`,
        ucc.assignees.length > 0 && `Assigned to ${ucc.assignees.map(a => a.name).join(', ')}`]
    return (
        <div className={styles.uccRow}>
            <button type='button' className={styles.uccRowHeader} onClick={() => setOpen(v => !v)} aria-expanded={open}>
                <ExpandMoreIcon className={cx(styles.uccChevron, !open && styles.chevronClosed)} />
                <div className={styles.uccMain}>
                    <div className={styles.uccLender}>
                        {lender || 'Lender not listed'}
                        {coLenders.length > 0 && <span className={styles.uccMore}> and {plural(coLenders.length, 'other')}</span>}
                    </div>
                    <div className={styles.uccFacts}>{facts.filter(Boolean).join(' · ')}</div>
                </div>
                {!ended && (
                    <div className={styles.inlineBadges}>
                        {ucc.recent && <Badge tone='watch'>Recent</Badge>}
                        {ucc.collateral.allAssets && <Badge tone='watch'>All assets</Badge>}
                        {ucc.collateral.receivables && <Badge tone='watch'>Receivables</Badge>}
                        {!claimsReceivables && <Badge tone='neutral'>{ucc.collateral.text ? 'No receivables' : 'Collateral not stated'}</Badge>}
                    </div>
                )}
            </button>
            {open && (
                <dl className={styles.uccDetails}>
                    <dt>State</dt><dd>{ucc.stateName || '---'}</dd>
                    <dt>Latest change</dt><dd>{ucc.change || 'None since filing'}</dd>
                    {!ended && !ucc.expires && <><dt>Expires</dt><dd>Not listed</dd></>}
                    <dt>Collateral</dt><dd>{ucc.collateral.plain || 'Not stated in the filing'}</dd>
                    <PartyDetails label={ucc.lenders.length > 1 ? 'Lenders' : 'Lender'} parties={ucc.lenders} />
                    <PartyDetails label='Assigned to' parties={ucc.assignees} />
                    <PartyDetails label={ucc.debtors.length > 1 ? 'Debtors' : 'Debtor'} parties={ucc.debtors} />
                    {ucc.office && <><dt>Filing office</dt><dd>{ucc.office}</dd></>}
                    <dt>Filing number</dt><dd className={styles.mono}>{ucc.number || '---'}</dd>
                </dl>
            )}
        </div>
    )
}

const ExperianUccRow = ({ filing }) => {
    const [open, setOpen] = useState(false)
    return (
        <div className={styles.uccRow}>
            <button type='button' className={styles.uccRowHeader} onClick={() => setOpen(v => !v)} aria-expanded={open}>
                <ExpandMoreIcon className={cx(styles.uccChevron, !open && styles.chevronClosed)} />
                <div className={styles.uccMain}>
                    <div className={styles.uccLender}>{filing.lender || (filing.dateLabel ? `Filed ${filing.dateLabel}` : 'Filing date not reported')}</div>
                    <div className={styles.uccFacts}>
                        {[filing.lender && filing.dateLabel && `Filed ${filing.dateLabel}`, filing.state, filing.actions.filter(a => a !== 'Filed').join(', ')].filter(Boolean).join(' · ')}
                    </div>
                </div>
                {!filing.ended && (
                    <div className={styles.inlineBadges}>
                        {filing.recent && <Badge tone='watch'>Recent</Badge>}
                        {filing.receivables && <Badge tone='watch'>Receivables</Badge>}
                        {!filing.receivables && <Badge tone='neutral'>{filing.collateral.length ? 'No receivables' : 'Collateral not stated'}</Badge>}
                    </div>
                )}
            </button>
            {open && (
                <dl className={styles.uccDetails}>
                    <dt>Lender</dt><dd>{filing.lender || 'Not reported by Experian'}</dd>
                    <dt>Actions</dt><dd>{filing.actions.join(', ') || '---'}</dd>
                    <dt>Collateral</dt><dd>{filing.collateral.join(', ') || 'Not stated'}</dd>
                    {filing.assignee && <><dt>Assigned to</dt><dd>{filing.assignee}</dd></>}
                    {filing.office && <><dt>Filing office</dt><dd>{filing.office}</dd></>}
                    <dt>File number</dt><dd className={styles.mono}>{filing.number || '---'}</dd>
                    {filing.documents.length > 0 && <><dt>Documents</dt><dd className={styles.mono}>{filing.documents.join(', ')}</dd></>}
                </dl>
            )}
        </div>
    )
}

const PartyDetails = ({ label, parties }) => (parties.length > 0 && (
    <>
        <dt>{label}</dt>
        <dd>{parties.map(p => <div key={p.name}>{p.name}{[p.address, p.ssn && `SSN ${p.ssn}`].filter(Boolean).map(extra => <span key={extra} className={styles.uccAddress}> · {extra}</span>)}</div>)}</dd>
    </>
))

const UccList = ({ rows, ended, experian }) => {
    const [showAll, setShowAll] = useState(false)
    const visible = showAll ? rows : rows.slice(0, PREVIEW_ROWS)
    return (
        <>
            <div className={styles.uccList}>
                {visible.map((u, i) => (experian ? <ExperianUccRow key={`${u.number}-${i}`} filing={u} /> : <UccRow key={`${u.number}-${i}`} ucc={u} ended={ended} />))}
            </div>
            {rows.length > PREVIEW_ROWS && (
                <div className={styles.listActions}>
                    <button type='button' className={styles.textButton} onClick={() => setShowAll(v => !v)}>
                        {showAll ? 'Show fewer' : `Show all (${rows.length})`}
                    </button>
                </div>
            )}
        </>
    )
}

const UccGroup = ({ label, rows, ended, experian }) => {
    const [open, setOpen] = useState(false)
    return (
        <div>
            <button type='button' className={styles.uccToggle} onClick={() => setOpen(v => !v)} aria-expanded={open}>
                <ExpandMoreIcon className={cx(styles.uccChevron, !open && styles.chevronClosed)} />
                <span>{label}</span>
            </button>
            {open && <UccList rows={rows} ended={ended} experian={experian} />}
        </div>
    )
}

const FilingHistory = ({ filing, label }) => {
    const [showAll, setShowAll] = useState(false)
    const notable = filing.history.filter(h => h.tone)
    const visible = showAll ? filing.history : notable
    if (!filing.history.length) return null
    return (
        <div className={styles.history}>
            <div className={styles.historyTitle}>{label} history</div>
            {visible.length > 0 ? visible.map((h, i) => (
                <div key={i} className={styles.historyRow}>
                    <span className={cx(styles.mono, styles.historyDate)}>{h.dateLabel || '---'}</span>
                    {h.tone ? <Badge tone={h.tone} dot>{h.description}</Badge> : <span>{h.description}</span>}
                </div>
            )) : <div className={styles.kvSub}>No dissolutions, forfeitures or reinstatements on record</div>}
            {filing.history.length > notable.length && (
                <button type='button' className={styles.textButton} onClick={() => setShowAll(v => !v)}>
                    {showAll ? 'Show fewer' : `Show all history (${filing.history.length})`}
                </button>
            )}
        </div>
    )
}

const FilingRow = ({ filing, showName }) => {
    const [open, setOpen] = useState(false)
    const title = [showName && filing.name, filing.stateName, filing.entity].filter(Boolean).join(' · ') || 'Filing'
    const facts = [filing.dateLabel && `Filed ${filing.dateLabel}${filing.dateType ? ` (${filing.dateType.toLowerCase()})` : ''}`,
        filing.number && `No. ${filing.number}`].filter(Boolean).join(' · ')
    return (
        <div className={styles.uccRow}>
            <button type='button' className={styles.uccRowHeader} onClick={() => setOpen(v => !v)} aria-expanded={open}>
                <ExpandMoreIcon className={cx(styles.uccChevron, !open && styles.chevronClosed)} />
                <div className={styles.uccMain}>
                    <div className={styles.uccLender}>{title}</div>
                    {facts && <div className={styles.uccFacts}>{facts}</div>}
                </div>
                <div className={styles.inlineBadges}>
                    <Badge tone={filing.tone}>{filing.status}</Badge>
                    {filing.goodStanding === true && <Badge tone='approved'>In good standing</Badge>}
                    {filing.goodStanding === false && <Badge tone='declined' dot>Not in good standing</Badge>}
                    {filing.term?.expired && <Badge tone='declined' dot>Term expired</Badge>}
                    {filing.historyWarning && <Badge tone='declined' dot>Warning on record</Badge>}
                </div>
            </button>
            {open && (
                <>
                    <dl className={styles.uccDetails}>
                        {(filing.nameType || filing.nameStatus) && <><dt>Name type</dt><dd>{[filing.nameType, filing.nameStatus].filter(Boolean).join(', ')}</dd></>}
                        <dt>Registered agent</dt>
                        <dd>{filing.agent ? <>{filing.agent.name}{filing.agent.address && <span className={styles.uccAddress}> · {filing.agent.address}</span>}</> : 'Not listed'}</dd>
                        {filing.purpose && <><dt>Purpose</dt><dd>{filing.purpose}</dd></>}
                        {filing.additionalInfo && <><dt>Additional info</dt><dd>{filing.additionalInfo}</dd></>}
                        {filing.forProfit && <><dt>For profit</dt><dd>{filing.forProfit}</dd></>}
                        {filing.foreign && <><dt>Home state</dt><dd>{filing.foreign}</dd></>}
                        {filing.stateOfOrigin && <><dt>State of origin</dt><dd>{filing.stateOfOrigin}</dd></>}
                        {filing.term && <><dt>Term of existence</dt><dd>{filing.term.label}</dd></>}
                        {filing.goodStanding === null && <><dt>Good standing</dt><dd>Not reported by the state</dd></>}
                        {filing.addresses.map((a, i) => <React.Fragment key={i}><dt>{a.type || 'Address'}</dt><dd>{a.address}</dd></React.Fragment>)}
                    </dl>
                    <div className={styles.filingHistoryBox}><FilingHistory filing={filing} label='Filing' /></div>
                </>
            )}
        </div>
    )
}

const RegistrationRow = ({ registration: g }) => {
    const [open, setOpen] = useState(false)
    const facts = [g.stateName, g.dateLabel && `Filed ${g.dateLabel}`, g.expires && !g.expired && !g.renewed && `Expires ${g.expires}`,
        g.renewed && `Expired ${g.expires}, renewed later`].filter(Boolean).join(' · ')
    return (
        <div className={styles.uccRow}>
            <button type='button' className={styles.uccRowHeader} onClick={() => setOpen(v => !v)} aria-expanded={open}>
                <ExpandMoreIcon className={cx(styles.uccChevron, !open && styles.chevronClosed)} />
                <div className={styles.uccMain}>
                    <div className={styles.uccLender}>{[g.type, g.otherName].filter(Boolean).join(' · ')}</div>
                    {facts && <div className={styles.uccFacts}>{facts}</div>}
                </div>
                <div className={styles.inlineBadges}>
                    {g.expired ? <Badge tone='declined' dot>Expired {g.expires}</Badge> : <Badge tone={g.tone}>{g.status}</Badge>}
                </div>
            </button>
            {open && (
                <dl className={styles.uccDetails}>
                    {g.name && <><dt>Name</dt><dd>{g.name}</dd></>}
                    {g.description && <><dt>Description</dt><dd>{g.description}</dd></>}
                    {g.entity && <><dt>Entity type</dt><dd>{g.entity}</dd></>}
                    {g.filingKind && g.filingKind !== g.type && <><dt>Filing</dt><dd>{g.filingKind}</dd></>}
                    <dt>Status reported</dt><dd>{g.reportedStatus}</dd>
                    <dt>Filing number</dt><dd className={styles.mono}>{g.number || '---'}</dd>
                    {g.address && <><dt>Address</dt><dd>{g.address}</dd></>}
                    {g.phone && <><dt>Phone</dt><dd>{g.phone}</dd></>}
                    {g.expires && <><dt>Expires</dt><dd>{g.expires}</dd></>}
                    {g.recordDate && <><dt>Last seen</dt><dd>{g.recordDate}</dd></>}
                    {g.processDate && <><dt>Processed</dt><dd>{g.processDate}</dd></>}
                </dl>
            )}
        </div>
    )
}

const CONNECTED_KIND = { Business: 'Connected business', Associate: 'Business associate', 'Parent company': 'Parent company' }
const ConnectedRow = ({ item: c, onGetReport, loading, cost }) => {
    const [open, setOpen] = useState(false)
    const how = c.kind === 'Associate' && c.role ? `Linked through ${c.role}` : CONNECTED_KIND[c.kind]
    return (
        <div className={styles.uccRow}>
            <div className={styles.connectedHead}>
                <button type='button' className={styles.uccRowHeader} onClick={() => setOpen(v => !v)} aria-expanded={open}>
                    <ExpandMoreIcon className={cx(styles.uccChevron, !open && styles.chevronClosed)} />
                    <div className={styles.uccMain}>
                        <div className={styles.uccLender}>{c.name}</div>
                        <div className={cx(styles.uccFacts, styles.oneLine)}>{[how, c.place].filter(Boolean).join(' · ')}</div>
                    </div>
                    <div className={styles.inlineBadges}>
                        {c.einRelation && <Badge tone='watch' dot>{c.einRelation}</Badge>}
                        {c.flagged && <Badge tone='watch' dot>{c.flagLabel}</Badge>}
                        {c.hasRecords && <Badge tone='declined' dot>Derogatory records</Badge>}
                        {c.disconnected && <Badge tone='watch' dot>Phone disconnected</Badge>}
                        {c.requested && !onGetReport && <Badge tone='neutral'>Requested</Badge>}
                    </div>
                </button>
                {onGetReport && (
                    <div className={styles.connectedAction}>
                        {c.raw && (
                            <Button className={styles.noWrap} variant='outlined' color='primary' size='small' fullWidth disabled={loading}
                                onClick={() => onGetReport(c.raw, c.index)}
                                endIcon={cost && !c.requested ? <CreditCost cost={cost} /> : undefined}>
                                {loading ? <CircularProgress size={20} color='inherit' /> : c.requested ? bgvString.VIEW_REPORT : strings.GET_REPORT}
                            </Button>
                        )}
                    </div>
                )}
            </div>
            {open && (
                <dl className={styles.uccDetails}>
                    <dt>Relationship</dt><dd>{how}</dd>
                    <dt>Address</dt><dd>{c.address || 'Not listed'}</dd>
                    {c.kind === 'Business' && <><dt>EIN</dt><dd className={styles.mono}>{c.ein || 'Not listed'}</dd></>}
                    {c.phone && <><dt>Phone</dt><dd>{c.phone}{c.disconnected ? ' (disconnected)' : ''}</dd></>}
                    {c.requested && <><dt>Report</dt><dd>Already requested{c.requestedDate ? ` on ${c.requestedDate}` : ''}; opening it again is free</dd></>}
                </dl>
            )}
        </div>
    )
}

export const AssetRow = ({ item }) => {
    const [open, setOpen] = useState(false)
    return (
        <div className={styles.uccRow}>
            <button type='button' className={styles.uccRowHeader} onClick={() => setOpen(v => !v)} aria-expanded={open}>
                <ExpandMoreIcon className={cx(styles.uccChevron, !open && styles.chevronClosed)} />
                <div className={styles.uccMain}>
                    <div className={styles.uccLender}>{item.title}</div>
                    {item.facts && <div className={styles.uccFacts}>{item.facts}</div>}
                </div>
                {item.chips.length > 0 && (
                    <div className={styles.inlineBadges}>
                        {item.chips.map(c => <Badge key={c.label} tone={c.tone} dot={c.tone === 'declined'}>{c.label}</Badge>)}
                    </div>
                )}
            </button>
            {open && (
                <dl className={styles.uccDetails}>
                    {item.details.length ? item.details.map((d, i) => <React.Fragment key={i}><dt>{d.label}</dt><dd>{d.value}</dd></React.Fragment>)
                        : <><dt>Details</dt><dd>No further details on file</dd></>}
                </dl>
            )}
            {open && [...(item.blocks || []), ...[].concat(item.more || [])].filter(Boolean).map((block, b) => (
                <div key={b} className={styles.detailBlock}>
                    <div className={styles.detailBlockTitle}>
                        {block.title}
                        {block.hint && (
                            <Tooltip title={block.hint} placement='top' arrow>
                                <InfoOutlinedIcon className={styles.detailBlockHint} aria-label={block.hint} />
                            </Tooltip>
                        )}
                    </div>
                    {block.details.length > 0 && (
                        <dl className={styles.uccDetails}>
                            {block.details.map((d, i) => <React.Fragment key={i}><dt>{d.label}</dt><dd>{d.value}</dd></React.Fragment>)}
                        </dl>
                    )}
                </div>
            ))}
        </div>
    )
}

const lenderItem = (u, ended) => {
    const [debtor, ...others] = u.debtors
    const parties = (list) => list.map(p => [p.name, p.address, p.ssn && `SSN ${p.ssn}`].filter(Boolean).join(' · ')).join('; ')
    return {
        title: debtor ? `Lent to ${debtor.name}${others.length ? ` and ${plural(others.length, 'other')}` : ''}` : 'Borrower not listed',
        facts: [u.dateLabel && `Filed ${u.dateLabel}`, ended ? u.change : u.expires && `Expires ${u.expires}`].filter(Boolean).join(' · '),
        chips: u.collateral.summary ? [{ label: `Collateral: ${u.collateral.summary}`, tone: 'neutral' }] : [],
        details: [
            ['State', u.stateName], ['Latest change', u.change || 'None since filing'], ['Expires', !ended && (u.expires || 'Not listed')],
            ['Collateral', u.collateral.plain || 'Not stated in the filing'], ['Borrowers', parties(u.debtors)], ['Secured party', parties(u.lenders)],
            ['Assigned to', parties(u.assignees)], ['Filing office', u.office], ['Filing number', u.number],
        ].filter(([, v]) => v).map(([label, value]) => ({ label, value })),
    }
}

export const RecordList = ({ items }) => {
    const [showAll, setShowAll] = useState(false)
    return (
        <>
            <div className={styles.uccList}>{(showAll ? items : items.slice(0, PREVIEW_ROWS)).map((item, i) => <AssetRow key={i} item={item} />)}</div>
            {items.length > PREVIEW_ROWS && (
                <div className={styles.listActions}>
                    <button type='button' className={styles.textButton} onClick={() => setShowAll(v => !v)}>
                        {showAll ? 'Show fewer' : `Show all (${items.length})`}
                    </button>
                </div>
            )}
        </>
    )
}

const AssetGroup = ({ group }) => {
    const [showAll, setShowAll] = useState(false)
    const [showPrior, setShowPrior] = useState(false)
    const visibleItems = showAll ? group.current : group.current.slice(0, PREVIEW_ROWS)
    return (
        <div>
            <div className={styles.uccGroupTitle}>{group.label} · {group.current.length} current{group.prior.length ? `, ${group.prior.length} prior` : ''}</div>
            {group.current.length > 0 && <div className={styles.uccList}>{visibleItems.map((item, i) => <AssetRow key={i} item={item} />)}</div>}
            {group.current.length > PREVIEW_ROWS && (
                <div className={styles.listActions}>
                    <button type='button' className={styles.textButton} onClick={() => setShowAll(v => !v)}>
                        {showAll ? 'Show fewer' : `Show all (${group.current.length})`}
                    </button>
                </div>
            )}
            {group.prior.length > 0 && (
                <div>
                    <button type='button' className={styles.uccToggle} onClick={() => setShowPrior(v => !v)} aria-expanded={showPrior}>
                        <ExpandMoreIcon className={cx(styles.uccChevron, !showPrior && styles.chevronClosed)} />
                        <span>{plural(group.prior.length, group.priorLabel)}</span>
                    </button>
                    {showPrior && <div className={styles.uccList}>{group.prior.map((item, i) => <AssetRow key={i} item={item} />)}</div>}
                </div>
            )}
        </div>
    )
}

const SupplierRow = ({ account: a }) => {
    const [open, setOpen] = useState(false)
    const lateLabel = a.late == null ? '' : a.late === 0 ? 'On time' : `${a.late}% paid late`
    return (
        <div className={styles.uccRow}>
            <button type='button' className={styles.uccRowHeader} onClick={() => setOpen(v => !v)} aria-expanded={open}>
                <ExpandMoreIcon className={cx(styles.uccChevron, !open && styles.chevronClosed)} />
                <div className={styles.uccMain}>
                    <div className={styles.uccLender}>{a.category}</div>
                    <div className={cx(styles.uccFacts, styles.oneLine)}>{[a.type, a.terms, a.balance && `Balance ${a.balance}`].filter(Boolean).join(' · ')}</div>
                </div>
                <div className={styles.inlineBadges}>
                    {a.isNew && <Badge tone='neutral'>New</Badge>}
                    {lateLabel && <Badge tone={a.tone} dot={a.tone === 'declined'}>{lateLabel}</Badge>}
                </div>
            </button>
            {open && (
                <dl className={styles.uccDetails}>
                    {a.terms && <><dt>Terms</dt><dd>{a.terms}</dd></>}
                    {a.balance && <><dt>Balance</dt><dd>{a.balance}</dd></>}
                    {a.highCredit && <><dt>Recent high credit</dt><dd>{a.highCredit}</dd></>}
                    {a.aging.length > 0 && <><dt>Paid</dt><dd>{a.aging.filter(x => x.percent).map(x => `${x.label} ${x.percent}%`).join(' · ')}</dd></>}
                    {a.reported && <><dt>Reported</dt><dd>{a.reported}</dd></>}
                    {a.lastActivity && <><dt>Last activity</dt><dd>{a.lastActivity}</dd></>}
                    {a.comments && <><dt>Comments</dt><dd>{a.comments}</dd></>}
                </dl>
            )}
        </div>
    )
}

function LimitedHistory({ info, allClear, ownerAction, onOwnerAction, suggestedReports, onOpenReport }) {
    const signals = [
        info.flaggedPeople > 0 && { text: `${plural(info.flaggedPeople, 'person', 'people')} with derogatory records`, warn: true },
        info.executivesElsewhere > 0 && { text: `${info.executivesElsewhere} ${info.executivesElsewhere === 1 ? 'person runs' : 'people run'} another business` },
        info.linkedPeople > 0 && { text: `${plural(info.linkedPeople, 'linked person', 'linked people')}` },
        info.connected > 0 && { text: `${plural(info.connected, 'connected business', 'connected businesses')}` },
    ].filter(Boolean)
    return (
        <div className={styles.limitedHistory} role='status'>
            <div>
                <strong>Limited history.</strong> This business is {info.age}. Liens, UCC filings and payment records take time to appear,
                so {allClear ? 'finding none' : 'a short report'} doesn't mean a clean history.
            </div>
            <div className={styles.limitedBlock}>
                <div className={styles.limitedTitle}>Check the owners</div>
                <div className={styles.limitedText}>
                    {signals.length ? signals.map((s, i) => (
                        <span key={i} className={s.warn ? styles.limitedWarn : undefined}>{i > 0 && ' · '}{s.text}</span>
                    )) : info.people ? `${plural(info.people, 'person', 'people')} listed in this report` : 'No people listed in this report'}
                </div>
                {ownerAction && onOwnerAction && (
                    <Button size='small' variant='outlined' color='primary' className={styles.limitedButton} onClick={onOwnerAction}>{ownerAction}</Button>
                )}
            </div>
            {suggestedReports?.length > 0 && onOpenReport && (
                <div className={styles.limitedBlock}>
                    <div className={styles.limitedTitle}>Reports that work for new businesses</div>
                    <ul className={styles.limitedReports}>
                        {suggestedReports.map(r => (
                            <li key={r.id}>
                                <button type='button' className={styles.limitedLink} onClick={() => onOpenReport(r.id)}>{r.label}</button>
                                <span className={styles.limitedText}> · {r.reason}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    )
}

function BusinessDetailReport({ report, applicationEin, onGetConnectedReport, connectedLoadingIndex, connectedCost,
    ownerAction, onOwnerAction, suggestedReports, onOpenReport }) {
    const model = useMemo(() => buildBusinessReport(report, applicationEin), [report, applicationEin])
    const idPrefix = useRef(`business-report-${Math.random().toString(36).slice(2)}`).current
    const [closed, setClosed] = useState({ raw: true })
    const [uccSource, setUccSource] = useState('lexis')
    const [peopleSource, setPeopleSource] = useState('lexis')
    const [showAllRegistrations, setShowAllRegistrations] = useState(false)
    const [showAllConnected, setShowAllConnected] = useState(false)
    const [showProfileDetails, setShowProfileDetails] = useState(false)
    const [showAllAccounts, setShowAllAccounts] = useState(false)
    const [showPaymentDetails, setShowPaymentDetails] = useState(false)
    const [recordsSource, setRecordsSource] = useState('lexis')
    const [showLenderEnded, setShowLenderEnded] = useState(false)
    const [moreAnchor, setMoreAnchor] = useState(null)

    const { summary, tiles, sections, allClear } = model
    const sectionById = Object.fromEntries(sections.map(s => [s.id, s]))
    const isOpen = id => !closed[id]
    const toggle = id => setClosed(prev => ({ ...prev, [id]: !prev[id] }))
    const domId = id => `${idPrefix}-${id}`
    const jumpTo = id => {
        setClosed(prev => ({ ...prev, [id]: false }))
        setMoreAnchor(null)
        setTimeout(() => document.getElementById(domId(id))?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0)
    }

    const visible = id => sectionById[id]?.hasData || (id === 'records' && !allClear)
    const mainLinks = sections.filter(s => s.main && s.hasData)
    const moreLinks = sections.filter(s => !s.main && s.hasData)
    const showLinks = mainLinks.length >= 3

    const sectionProps = id => ({ id: domId(id), title: sectionById[id].title, open: isOpen(id), onToggle: () => toggle(id) })

    if (!model.hasData) {
        return (
            <div className={styles.body}>
                <div className={styles.calmLine}>This report has no business data. Close it and open the report again, or run a new search.</div>
            </div>
        )
    }

    return (
        <div className={styles.body}>
            <div className={styles.summary}>
                <div className={styles.identity}>
                    <h2 className={styles.companyName}>{summary.name || '---'}</h2>
                    {summary.address && <div className={styles.summaryLine}>{summary.address}</div>}
                    {(summary.phone || summary.website) && (
                        <div className={styles.summaryLine}>{[summary.phone, summary.website].filter(Boolean).join(' · ')}</div>
                    )}
                    {summary.aliases.length > 0 && (
                        <div className={styles.summaryLine}>
                            Also known as {summary.aliases.slice(0, 2).map(a => a.name).join(', ')}
                            {summary.aliases.length > 2 ? ` and ${summary.aliases.length - 2} more` : ''}
                        </div>
                    )}
                    <div className={styles.summaryBadges}>
                        {summary.ageLabel && !model.limitedHistory && <Badge tone={summary.isNew ? 'watch' : 'approved'} dot={summary.isNew}>{summary.ageLabel}</Badge>}
                        {summary.incorporation && (
                            <Badge tone={summary.incorporation.tone} dot>
                                {[summary.incorporation.label, summary.incorporation.status].filter(Boolean).join(', ')}
                            </Badge>
                        )}
                        {summary.notActive && <Badge tone='declined' dot>Not active</Badge>}
                        {summary.ein.mismatch && <Badge tone='watch' dot>EIN differs from application</Badge>}
                        {summary.phoneDisconnected && <Badge tone='watch' dot>Phone disconnected</Badge>}
                    </div>
                    <div className={styles.summaryFacts}>
                        {summary.industry && (
                            <div>
                                <div className={styles.factLabel}>Industry</div>
                                <div className={styles.factValue}>{summary.industry}</div>
                                {summary.industryDescription && <div className={styles.einOthers}>{summary.industryDescription}</div>}
                            </div>
                        )}
                        <div>
                            <div className={styles.factLabel}>EIN</div>
                            <div className={cx(styles.factValue, summary.ein.value && styles.mono)}>{summary.ein.value || 'EIN not found'}</div>
                            {summary.ein.mismatch && (
                                <div className={styles.einNote}>
                                    Application lists {summary.ein.applicationValue}
                                    {summary.ein.applicationLinked && ', which Lexis also links to this company'}
                                </div>
                            )}
                            {summary.ein.others.some(o => !o.matchesApplication) && (
                                <div className={styles.einOthers}>
                                    Also linked: {summary.ein.others.filter(o => !o.matchesApplication).map(o => [o.value, o.name && `(${o.name})`].filter(Boolean).join(' ')).join(', ')}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className={styles.tiles}>
                    {tiles.map(t => {
                        const content = (
                            <>
                                <span className={styles.tileLabel}>{t.label}</span>
                                <span className={cx(styles.tileValue, typeof t.value !== 'number' && styles.tileValueText)}>
                                    {t.value}{t.unit && <span className={styles.tileUnit}> {t.unit}</span>}
                                </span>
                                <span className={styles.tileSub}>{t.sub}</span>
                            </>
                        )
                        return visible(t.section) ? (
                            <button key={t.label} type='button' className={cx(styles.tile, styles.tileButton, t.flag && styles.tileFlag)} onClick={() => jumpTo(t.target || t.section)}>
                                {content}
                            </button>
                        ) : (
                            <div key={t.label} className={cx(styles.tile, t.flag && styles.tileFlag)}>{content}</div>
                        )
                    })}
                </div>
            </div>

            {showLinks && (
                <nav className={styles.links} aria-label='Report sections'>
                    {mainLinks.map(s => (
                        <button key={s.id} type='button' className={styles.link} onClick={() => jumpTo(s.id)}>
                            <span>{s.linkTitle || s.title}</span>
                            {s.tone ? <Badge tone={s.tone}>{s.count}</Badge> : <span className={styles.linkCount}>{s.count}</span>}
                        </button>
                    ))}
                    {moreLinks.length > 0 && (
                        <>
                            <button type='button' className={cx(styles.link, styles.moreLink)} onClick={e => setMoreAnchor(e.currentTarget)}
                                aria-haspopup='menu' aria-expanded={Boolean(moreAnchor)}>
                                More <ExpandMoreIcon fontSize='small' />
                            </button>
                            <Menu anchorEl={moreAnchor} open={Boolean(moreAnchor)} onClose={() => setMoreAnchor(null)}
                                getContentAnchorEl={null} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                                {moreLinks.map(s => (
                                    <MenuItem key={s.id} onClick={() => jumpTo(s.id)}>
                                        {s.title}{s.count ? ` (${s.count})` : ''}
                                    </MenuItem>
                                ))}
                            </Menu>
                        </>
                    )}
                </nav>
            )}

            {model.limitedHistory && (
                <LimitedHistory info={model.limitedHistory} allClear={allClear} ownerAction={ownerAction} onOwnerAction={onOwnerAction}
                    suggestedReports={suggestedReports} onOpenReport={onOpenReport} />
            )}

            {allClear && !model.limitedHistory && (
                <div className={styles.allClear} role='status'>
                    <VerifiedUserIcon className={styles.allClearIcon} />
                    <div>
                        <strong>Nothing to flag.</strong> No active UCC filings, no people with records, no bankruptcies, liens or judgments,
                        and no registration problems found.
                    </div>
                </div>
            )}

            {visible('ucc') && (
                <Section {...sectionProps('ucc')} summary={model.ucc.active.length ? (
                    <>
                        <span className={styles.cardSummary}>
                            {model.ucc.active.length} active · {plural(model.ucc.securedParties, 'lender')}
                        </span>
                        {model.ucc.recent > 0 && <Badge tone='watch' dot>{model.ucc.recent} recent</Badge>}
                        {model.ucc.receivables > 0 && <Badge tone='watch' dot>{model.ucc.receivables} on receivables</Badge>}
                    </>
                ) : <span className={styles.cardSummary}>No active filings</span>}>
                    {model.ucc.experian.active.length + model.ucc.experian.ended.length > 0 && (
                        <div className={styles.sourceTabs} role='tablist'>
                            {[['lexis', 'Lexis', model.ucc.active.length], ['experian', 'Experian', model.ucc.experian.active.length]].map(([key, label, count]) => (
                                <button key={key} type='button' role='tab' aria-selected={uccSource === key}
                                    className={cx(styles.sourceTab, uccSource === key && styles.sourceTabActive)} onClick={() => setUccSource(key)}>
                                    {label} <span className={styles.sourceCount}>{count} active</span>
                                </button>
                            ))}
                        </div>
                    )}
                    {model.ucc.experian.collateral && (
                        <div className={styles.note}>
                            Experian
                            {model.ucc.experian.collateral.filings ? ` counts ${plural(model.ucc.experian.collateral.filings, 'UCC filing')}` : ''}
                            {model.ucc.experian.collateral.kinds.length ? `${model.ucc.experian.collateral.filings ? ', with' : ' lists'} collateral on ${model.ucc.experian.collateral.kinds.join(', ').replace(/, ([^,]*)$/, ' and $1')}` : ''}.
                        </div>
                    )}
                    {uccSource === 'experian' ? (
                        <>
                            <div className={styles.note}>Experian's own list of UCC filings. It may overlap with the Lexis list and rarely names the lender.</div>
                            {model.ucc.experian.active.length > 0 && <UccList rows={model.ucc.experian.active} experian />}
                            {!model.ucc.experian.active.length && <div className={styles.note}>No active filings.</div>}
                            {model.ucc.experian.ended.length > 0 && (
                                <UccGroup rows={model.ucc.experian.ended} experian label={plural(model.ucc.experian.ended.length, 'ended filing')} />
                            )}
                        </>
                    ) : (
                        <>
                        {model.ucc.active.length > 0 && <UccList rows={model.ucc.active} />}
                        {!model.ucc.active.length && <div className={styles.note}>No active filings.</div>}
                        {model.ucc.ended.length > 0 && <UccGroup rows={model.ucc.ended} ended label={plural(model.ucc.ended.length, 'ended filing')} />}
                        </>
                    )}
                </Section>
            )}

            {visible('people') && (
                <Section {...sectionProps('people')} summary={
                    <Badge tone={model.people.flagged || model.people.deceasedCurrent ? 'declined' : undefined} dot={Boolean(model.people.flagged || model.people.deceasedCurrent)}>
                        {plural(model.people.rows.length, 'person', 'people')}{model.people.flagged ? ` · ${model.people.flagged} with derogatory records` : ''}
                        {model.people.deceasedCurrent ? ` · ${model.people.deceasedCurrent} current listed as deceased` : ''}
                    </Badge>
                }>
                    {model.people.experian.length > 0 && (
                        <div className={styles.sourceTabs} role='tablist'>
                            {[['lexis', 'Lexis', model.people.rows.length], ['experian', 'Experian', model.people.experian.length]].map(([key, label, count]) => (
                                <button key={key} type='button' role='tab' aria-selected={peopleSource === key}
                                    className={cx(styles.sourceTab, peopleSource === key && styles.sourceTabActive)} onClick={() => setPeopleSource(key)}>
                                    {label} <span className={styles.sourceCount}>{plural(count, 'person', 'people')}</span>
                                </button>
                            ))}
                        </div>
                    )}
                    {peopleSource === 'experian' ? (
                        <>
                            <div className={styles.note}>Experian's own list of officers. Titles as Experian reports them; no records check.</div>
                            <ListTable rows={model.people.experian} columns={[
                                { label: 'Name', text: o => o.name, render: o => (
                                    <div className={styles.cellStack}>
                                        <span>{o.name}</span>
                                        {o.ssn && <span className={styles.kvSub}>SSN {o.ssn}</span>}
                                    </div>
                                ) },
                                { label: 'Title', text: o => o.titles.join(' '), render: o => o.titles.join(', ') || '---' },
                                { label: 'In Lexis', text: o => (o.inLexis ? 'Also listed by Lexis' : 'Only in Experian'),
                                    render: o => <Badge tone='neutral'>{o.inLexis ? 'Also listed by Lexis' : 'Only in Experian'}</Badge> },
                            ]} />
                        </>
                    ) : (
                        <>
                        <ListTable rows={model.people.rows} columns={[
                            { label: 'Name', text: p => [p.name, p.elsewhere].join(' '), render: p => (
                                <div className={styles.cellStack}>
                                    <span>{p.name}</span>
                                    {p.deceased && <Badge tone={p.current ? 'declined' : 'neutral'} dot={p.current}>Deceased</Badge>}
                                    {p.elsewhere && <span className={styles.kvSub}>Also an executive at {p.elsewhere}</span>}
                                    {p.address && <span className={styles.kvSub}>{p.address}</span>}
                                    {p.ssn && <span className={styles.kvSub}>SSN {p.ssn}</span>}
                                </div>
                            ) },
                            { label: 'Title', text: p => [p.title, ...p.otherRoles.map(r => r.title)].join(' '), render: p => (
                                <div className={styles.cellStack}>
                                    <span>{p.title || '---'}</span>
                                    {p.otherRoles.map(r => (
                                                <span key={`${r.title}${r.period}`} className={styles.kvSub}>
                                                    {r.title ? `${r.current ? 'Currently also' : 'Also'} ${r.title}` : `${r.current ? 'Current' : 'Other'} role, no title`}{r.period && ` · ${r.period}`}
                                                </span>
                                            ))}
                                </div>
                            ) },
                            { label: 'Status', text: p => [p.current ? 'Current' : 'Prior', p.period].join(' '),
                                render: p => (
                                    <div className={styles.cellStack}>
                                        <Badge tone={p.current ? 'approved' : 'neutral'}>{p.current ? 'Current' : 'Prior'}</Badge>
                                        {p.period && <span className={styles.kvSub}>{p.period}</span>}
                                    </div>
                                ) },
                            { label: 'Records', text: p => (p.hasRecords ? 'Derogatory records' : ''),
                                render: p => (p.hasRecords ? (
                                    <Tooltip title={DEROGATORY_HINT} placement='top' arrow>
                                        <span style={{ cursor: 'help' }}><Badge tone='declined' dot>Derogatory records</Badge></span>
                                    </Tooltip>
                                ) : null) },
                        ]} />
                        {model.people.linked.length > 0 && (
                            <>
                                <div className={styles.note}>Other people linked to the company through records such as UCC filings, property or vehicles.</div>
                                <ListTable rows={model.people.linked} columns={[
                                    { label: 'Name', text: p => [p.name, p.address].join(' '), render: p => (
                                        <div className={styles.cellStack}>
                                            <span>{p.name}</span>
                                            {p.deceased && <Badge tone='neutral'>Deceased</Badge>}
                                            {p.address && <span className={styles.kvSub}>{p.address}</span>}
                                            {p.ssn && <span className={styles.kvSub}>SSN {p.ssn}</span>}
                                        </div>
                                    ) },
                                    { label: 'Linked through', text: p => p.via, render: p => p.via || '---' },
                                    { label: 'Period', text: p => p.period, render: p => p.period || '---' },
                                    { label: 'Records', text: p => (p.hasRecords ? 'Derogatory records' : ''),
                                        render: p => (p.hasRecords ? (
                                            <Tooltip title={DEROGATORY_HINT} placement='top' arrow>
                                                <span style={{ cursor: 'help' }}><Badge tone='declined' dot>Derogatory records</Badge></span>
                                            </Tooltip>
                                        ) : null) },
                                ]} />
                            </>
                        )}
                        </>
                    )}
                </Section>
            )}

            {visible('filings') && (
                <Section {...sectionProps('filings')} summary={model.filings.notActive || model.filings.openWarnings
                    ? <Badge tone='declined' dot>{[model.filings.notActive && `${plural(model.filings.notActive, 'filing')} not active`,
                        model.filings.openWarnings && `${plural(model.filings.openWarnings, 'warning')} on record`].filter(Boolean).join(' · ')}</Badge>
                    : <span className={styles.cardSummary}>{plural(model.filings.rows.length, 'filing')}</span>}>
                    <div className={styles.uccList}>
                        {model.filings.rows.map((f, i) => <FilingRow key={i} filing={f} showName={model.filings.showNames} />)}
                    </div>
                    {model.filings.agents.length > 0 && (
                        <>
                            <div className={styles.note}>Registered agents on file, latest first.</div>
                            <ListTable rows={model.filings.agents} columns={[
                                { label: 'Agent', text: a => [a.name, a.address].join(' '), render: a => (
                                    <div className={styles.cellStack}>
                                        <span>{a.name}</span>
                                        {a.address && <span className={styles.kvSub}>{a.address}</span>}
                                    </div>
                                ) },
                                { label: 'Title', text: a => a.title, render: a => a.title || '---' },
                                { label: 'Period', text: a => a.period, render: a => a.period || '---' },
                            ]} />
                        </>
                    )}
                </Section>
            )}

            {visible('licenses') && (
                <Section {...sectionProps('licenses')} summary={model.registrations.some(g => g.expired)
                    ? <Badge tone='declined' dot>{plural(model.registrations.filter(g => g.expired).length, 'expired registration')}</Badge>
                    : <span className={styles.cardSummary}>{plural(model.registrations.length, 'registration')}</span>}>
                    <div className={styles.uccList}>
                        {model.registrations.slice(0, showAllRegistrations ? undefined : PREVIEW_ROWS).map((g, i) => <RegistrationRow key={i} registration={g} />)}
                    </div>
                    {model.registrations.length > PREVIEW_ROWS && (
                        <div className={styles.listActions}>
                            <button type='button' className={styles.textButton} onClick={() => setShowAllRegistrations(v => !v)}>
                                {showAllRegistrations ? 'Show fewer' : `Show all (${model.registrations.length})`}
                            </button>
                        </div>
                    )}
                </Section>
            )}

            {visible('connected') && (
                <Section {...sectionProps('connected')} summary={
                    <span className={styles.cardSummary}>
                        {[plural(model.connected.filter(c => c.kind === 'Business').length, 'business', 'businesses'),
                            plural(model.connected.filter(c => c.kind === 'Associate').length, 'associate'),
                            model.connected.some(c => c.kind === 'Parent company') && plural(model.connected.filter(c => c.kind === 'Parent company').length, 'parent company', 'parent companies'),
                        ].filter(Boolean).join(' · ')}
                    </span>
                }>
                    {onGetConnectedReport && <div className={styles.note}>{bgvString.REPORT_CHARGED_ONCE}</div>}
                    <div className={styles.uccList}>
                        {model.connected.slice(0, showAllConnected ? undefined : PREVIEW_ROWS).map((c, i) => (
                            <ConnectedRow key={i} item={c} onGetReport={onGetConnectedReport} loading={c.raw && connectedLoadingIndex === c.index} cost={connectedCost} />
                        ))}
                    </div>
                    {model.connected.length > PREVIEW_ROWS && (
                        <div className={styles.listActions}>
                            <button type='button' className={styles.textButton} onClick={() => setShowAllConnected(v => !v)}>
                                {showAllConnected ? 'Show fewer' : `Show all (${model.connected.length})`}
                            </button>
                        </div>
                    )}
                </Section>
            )}

            {visible('payments') && (
                <Section {...sectionProps('payments')} summary={model.payments.days != null ? (
                    <Badge tone={model.payments.tone} dot={model.payments.tone !== 'approved'}>
                        {model.payments.days === 0 ? 'Pays on time' : `Pays ${plural(model.payments.days, 'day')} late on average`}
                        {model.payments.worsening ? ' · getting later' : ''}
                    </Badge>
                ) : (
                    <span className={styles.cardSummary}>
                        {model.payments.trades || model.payments.accounts.length
                            ? plural(model.payments.trades || model.payments.accounts.length, 'supplier account')
                            : plural(model.payments.inquiries?.total || 0, 'credit check')}
                    </span>
                )}>
                    <div className={styles.note}>
                        How the company pays its suppliers, from Experian
                        {model.payments.trades ? ` (${plural(model.payments.trades, 'supplier account')})` : ''}.
                    </div>
                    <div className={styles.kvGrid}>
                        {[
                            ['Days late now', model.payments.days],
                            ['Predicted days late', model.payments.predicted],
                            ['Industry average', model.payments.industry],
                        ].filter(([, v]) => v != null).map(([label, v]) => (
                            <div key={label}>
                                <div className={styles.factLabel} style={{ color: 'var(--fg-tertiary)' }}>{label}</div>
                                <div className={styles.kvValue}>{plural(v, 'day')}</div>
                            </div>
                        ))}
                        {[
                            ['Trend', model.payments.trend],
                            ['Compared with industry', model.payments.performance],
                            ['Current balance', model.payments.balance],
                            ['Highest credit', model.payments.highCredit],
                        ].filter(([, v]) => v).map(([label, v]) => (
                            <div key={label}>
                                <div className={styles.factLabel} style={{ color: 'var(--fg-tertiary)' }}>{label}</div>
                                <div className={styles.kvValue}>
                                    {label === 'Trend' && model.payments.worsening ? <Badge tone='declined' dot>{v}</Badge> : v}
                                </div>
                            </div>
                        ))}
                    </div>
                    {model.payments.aging.length > 0 && (
                        <div className={styles.aging}>
                            <div className={styles.agingBar}>
                                {model.payments.aging.map((a, i) => a.percent > 0 && (
                                    <span key={a.label} className={styles[AGING_STYLES[i]]} style={{ width: `${a.percent}%` }} />
                                ))}
                            </div>
                            <div className={styles.agingLegend}>
                                {model.payments.aging.filter(a => a.percent).map(a => <span key={a.label}>{a.label} {a.percent}%</span>)}
                            </div>
                        </div>
                    )}
                    {model.payments.inquiries && (
                        <div className={styles.note}>
                            {plural(model.payments.inquiries.total, 'credit check')} by other companies
                            {model.payments.inquiries.period ? `, ${model.payments.inquiries.period}` : ''}
                            {model.payments.inquiries.recent ? ` (${model.payments.inquiries.recent} in the last 6 months)` : ''}.
                        </div>
                    )}
                    {model.payments.accounts.length > 0 && (
                        <div>
                            <div className={styles.uccGroupTitle}>
                                Supplier accounts with details · {model.payments.accounts.length}{model.payments.trades > model.payments.accounts.length ? ` of ${model.payments.trades}` : ''}
                                {model.payments.lateAccounts ? `, ${model.payments.lateAccounts} paid late` : ''}
                            </div>
                            <div className={styles.uccList}>
                                {model.payments.accounts.slice(0, showAllAccounts ? undefined : PREVIEW_ROWS).map((a, i) => <SupplierRow key={i} account={a} />)}
                            </div>
                            {model.payments.accounts.length > PREVIEW_ROWS && (
                                <div className={styles.listActions}>
                                    <button type='button' className={styles.textButton} onClick={() => setShowAllAccounts(v => !v)}>
                                        {showAllAccounts ? 'Show fewer' : `Show all (${model.payments.accounts.length})`}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                    {model.payments.history.length > 0 && (
                        <ListTable rows={model.payments.history} columns={[
                            { label: model.payments.historyByMonth ? 'Month' : 'Quarter', text: h => h.label, render: h => h.label || '---' },
                            { label: 'Days late', text: h => String(h.days ?? ''), render: h => (h.days == null ? '---'
                                : <Badge tone={dbtTone(h.days) === 'approved' ? 'neutral' : dbtTone(h.days)}>{h.days === 0 ? 'On time' : plural(h.days, 'day')}</Badge>) },
                            { label: 'Balance', text: h => String(h.balance ?? ''), render: h => (h.balance == null ? '---' : formatMoney(h.balance)), mono: true },
                            { label: 'Paid late', text: h => String(h.late ?? ''), render: h => (h.late == null ? '---' : `${h.late}%`), mono: true },
                        ]} />
                    )}
                    {(model.payments.summaryDetails.length > 0 || model.payments.inquiries) && (
                        <div>
                            <button type='button' className={styles.uccToggle} onClick={() => setShowPaymentDetails(v => !v)} aria-expanded={showPaymentDetails}>
                                <ExpandMoreIcon className={cx(styles.uccChevron, !showPaymentDetails && styles.chevronClosed)} />
                                <span>More payment details</span>
                            </button>
                            {showPaymentDetails && (
                                <dl className={styles.uccDetails}>
                                    {model.payments.summaryDetails.map(d => <React.Fragment key={d.label}><dt>{d.label}</dt><dd>{d.value}</dd></React.Fragment>)}
                                    {model.payments.inquiries && (
                                        <><dt>Credit checks by type</dt><dd>{model.payments.inquiries.byType.map(i => `${i.type} ${i.count}`).join(' · ')}</dd></>
                                    )}
                                </dl>
                            )}
                        </div>
                    )}
                </Section>
            )}

            {visible('records') && (sectionById.records.hasData ? (
                <Section {...sectionProps('records')} summary={model.derogatory.open || model.derogatory.bankruptcies || model.derogatory.experianOpen
                    ? <Badge tone='declined' dot>{[model.derogatory.bankruptcies && plural(model.derogatory.bankruptcies, 'bankruptcy', 'bankruptcies'),
                        model.derogatory.open && `${model.derogatory.open} open`,
                        model.derogatory.experianOpen && `${model.derogatory.experianOpen} more open in Experian`].filter(Boolean).join(' · ')}</Badge>
                    : <span className={styles.cardSummary}>{plural(model.derogatory.liens.length + model.derogatory.experianExtra, 'record')}, none open</span>}>
                    {model.derogatory.experian.length > 0 && (
                        <div className={styles.sourceTabs} role='tablist'>
                            {[['lexis', 'Lexis', model.derogatory.liens.length + model.derogatory.bankruptcyRows.length], ['experian', 'Experian', model.derogatory.experian.length]].map(([key, label, count]) => (
                                <button key={key} type='button' role='tab' aria-selected={recordsSource === key}
                                    className={cx(styles.sourceTab, recordsSource === key && styles.sourceTabActive)} onClick={() => setRecordsSource(key)}>
                                    {label} <span className={styles.sourceCount}>{plural(count, 'record')}</span>
                                </button>
                            ))}
                        </div>
                    )}
                    {recordsSource === 'experian' ? (
                        <>
                            <div className={styles.note}>Experian's own public records. They may overlap with the Lexis list.</div>
                            <RecordList items={model.derogatory.experian.map(x => ({
                                title: x.type && x.type.toLowerCase().includes(x.kind.toLowerCase()) ? x.type : [x.kind, x.type].filter(Boolean).join(' · '),
                                facts: [x.creditor && `Owed to ${x.creditor}`, x.amount, x.dateLabel && `Filed ${x.dateLabel}`, x.where].filter(Boolean).join(' · '),
                                chips: [{ label: x.open ? 'Open' : [x.action || 'Resolved', x.releasedLabel].filter(Boolean).join(' '), tone: x.open ? 'declined' : 'neutral' }, x.inLexis && { label: 'Also in Lexis', tone: 'neutral' }, x.disputed && { label: 'Disputed', tone: 'watch' }].filter(Boolean),
                                details: [['Status', x.action], ['Filed', x.dateLabel], ['Released', x.releasedLabel], ['Owed to', x.creditor], ['Amount', x.amount], ['Assets', x.assets], ['Where', x.where],
                                    ['Description', x.description], ['Document number', x.number], ['Disputed', x.disputed ? 'Yes, the company disputes this record' : '']]
                                    .filter(([, v]) => v).map(([label, value]) => ({ label, value })),
                            }))} />
                        </>
                    ) : (
                        <>
                            {model.derogatory.bankruptcyRows.length > 0 && (
                                <RecordList items={model.derogatory.bankruptcyRows.map(b => ({
                                    more: b.more,
                                    title: `Bankruptcy · ${b.type || 'chapter not reported'}`,
                                    facts: [b.filer, b.dateLabel && `Filed ${b.dateLabel}`, b.court].filter(Boolean).join(' · '),
                                    chips: [{ label: b.status || 'Status not reported', tone: b.statusTone }],
                                    details: b.details,
                                }))} />
                            )}
                            {!model.derogatory.bankruptcyRows.length && model.derogatory.bankruptcies > 0 && (
                                <div className={styles.note}><Badge tone='declined' dot>{plural(model.derogatory.bankruptcies, 'bankruptcy filing')} on record</Badge></div>
                            )}
                            {model.derogatory.liens.length > 0 && (
                                <RecordList items={model.derogatory.liens.map(l => ({
                                    more: l.more,
                                    title: l.type,
                                    facts: [l.creditor && `Owed to ${l.creditor}`, l.amount, l.dateLabel && `Filed ${l.dateLabel}`, l.state].filter(Boolean).join(' · '),
                                    chips: [
                                        { label: l.status === 'Released' && l.releaseDate ? `Released ${l.releaseDate}` : l.status, tone: l.status === 'Open' ? 'declined' : 'neutral' },
                                        l.eviction && { label: 'Eviction', tone: l.status === 'Open' ? 'declined' : 'neutral' },
                                    ].filter(Boolean),
                                    details: l.details,
                                }))} />
                            )}
                        </>
                    )}
                </Section>
            ) : (
                <section id={domId('records')} className={styles.card}>
                    <div className={styles.calmLine}><CheckIcon fontSize='small' />No bankruptcies, liens or judgments found</div>
                </section>
            ))}

            {visible('size') && (
                <Section {...sectionProps('size')} summary={model.changeAlerts.length > 0 && (
                    <div className={styles.inlineBadges}>
                        {model.changeAlerts.map(a => (
                            <Badge key={a.label} tone={a.recent ? 'watch' : 'neutral'} dot={a.recent}>{a.label}{a.date ? ` ${a.date}` : ''}</Badge>
                        ))}
                    </div>
                )}>
                    {model.estimates.length > 0 && (
                        <>
                            <div className={styles.note}>
                                {model.estimateSources.length > 0
                                    ? <>Size estimated by {model.estimateSources.join(', ').replace(/, ([^,]*)$/, ' and $1')}, not reported by the company
                                        {model.estimates.some(e => e.filed) ? ', except the benefit plan headcount, which the company filed with the IRS' : ''}.</>
                                    : 'Benefit plan headcount the company filed with the IRS.'}
                            </div>
                            {[model.estimates.filter(e => !/employees/i.test(e.label)), model.estimates.filter(e => /employees/i.test(e.label))]
                                .filter(row => row.length).map(row => (
                                    <div key={row[0].label} className={styles.kvGrid}>
                                        {row.map(e => (
                                            <div key={e.label} className={e.faded ? styles.faded : undefined}>
                                                <div className={styles.factLabel} style={{ color: 'var(--fg-tertiary)' }}>{e.label}</div>
                                                <div className={styles.kvValue}>{e.value}</div>
                                                {e.asOf && <div className={styles.kvSub}>{e.filed ? `IRS Form 5500, plan year ${e.asOf}` : `As of ${e.asOf}`}</div>}
                                            </div>
                                        ))}
                                    </div>
                                ))}
                        </>
                    )}
                    {model.growth.length > 0 && (
                        <div className={styles.kvGrid}>
                            {model.growth.map(g => (
                                <div key={g.label}>
                                    <div className={styles.factLabel} style={{ color: 'var(--fg-tertiary)' }}>{g.label}</div>
                                    <div className={styles.kvValue}>{g.value}</div>
                                </div>
                            ))}
                        </div>
                    )}
                    {model.profileDetails.length > 0 && (
                        <div>
                            <div className={styles.toggleWithHint}>
                                <button type='button' className={styles.uccToggle} onClick={() => setShowProfileDetails(v => !v)} aria-expanded={showProfileDetails}>
                                    <ExpandMoreIcon className={cx(styles.uccChevron, !showProfileDetails && styles.chevronClosed)} />
                                    <span>Company details ({model.profileDetails.length})</span>
                                </button>
                                <Tooltip title={PROFILE_DETAILS_HINT} placement='top' arrow>
                                    <InfoOutlinedIcon className={styles.detailBlockHint} aria-label={PROFILE_DETAILS_HINT} />
                                </Tooltip>
                            </div>
                            {showProfileDetails && (
                                <dl className={styles.uccDetails}>
                                    {model.profileDetails.map(d => (
                                        <React.Fragment key={d.label}><dt>{d.label}</dt><dd className={/DUNS/.test(d.label) ? styles.mono : undefined}>{d.value}</dd></React.Fragment>
                                    ))}
                                </dl>
                            )}
                        </div>
                    )}
                </Section>
            )}

            {visible('locations') && (
                <Section {...sectionProps('locations')} summary={
                    <>
                        <span className={styles.cardSummary}>
                            {plural(model.locations.rows.length, 'address', 'addresses')}{model.locations.states.length ? ` in ${plural(model.locations.states.length, 'state')}` : ''}
                        </span>
                        {model.locations.disconnected > 0 && <Badge tone='watch' dot>{model.locations.disconnected} with a disconnected phone</Badge>}
                    </>
                }>
                    <RecordList items={model.locations.rows} />
                </Section>
            )}

            {visible('assets') && (
                <Section {...sectionProps('assets')} summary={model.propertyDistress
                    ? <Badge tone='declined' dot>{plural(model.propertyDistress, 'property', 'properties')} in foreclosure or default</Badge>
                    : <span className={styles.cardSummary}>{model.assets.map(a => a.label.toLowerCase()).join(', ')}</span>}>
                    {model.assetGroups.length === 0 && (
                        <div className={styles.kvGrid}>
                            {model.assets.map(a => (
                                <div key={a.label}>
                                    <div className={styles.factLabel} style={{ color: 'var(--fg-tertiary)' }}>{a.label}</div>
                                    <div className={styles.kvValue}>{a.value}</div>
                                    {a.sub && <div className={styles.kvSub}>{a.sub}</div>}
                                </div>
                            ))}
                        </div>
                    )}
                    {model.assetGroups.map(g => <AssetGroup key={g.id} group={g} />)}
                </Section>
            )}

            {visible('professionalLicenses') && (
                <Section {...sectionProps('professionalLicenses')} summary={model.licenses.some(l => l.expired)
                    ? <Badge tone='declined' dot>{plural(model.licenses.filter(l => l.expired).length, 'expired license')}</Badge>
                    : <span className={styles.cardSummary}>{plural(model.licenses.length, 'license')}{model.licenses.some(l => l.medical) ? `, ${model.licenses.filter(l => l.medical).length} medical` : ''}</span>}>
                    <RecordList items={model.licenses.map(l => ({
                        title: l.description || 'License',
                        facts: [l.issuer, l.expires && !l.expired && !l.renewed && `Expires ${l.expires}`, l.renewed && `Expired ${l.expires}, renewed later`].filter(Boolean).join(' · '),
                        chips: [
                            l.medical && { label: 'Medical', tone: 'neutral' },
                            l.pending && { label: 'Pending', tone: 'watch' },
                            l.expired && { label: `Expired ${l.expires}`, tone: 'declined' },
                            l.expiresSoon && { label: 'Expires soon', tone: 'watch' },
                        ].filter(Boolean),
                        details: [['Issuer', l.issuer], ['Issued', l.issued], ['Expires', l.expires], ['License number', l.number],
                            ['Status', l.pending ? 'Application pending, not yet granted' : '']]
                            .filter(([, v]) => v).map(([label, value]) => ({ label, value })),
                    }))} />
                </Section>
            )}
            {visible('pension') && (
                <Section {...sectionProps('pension')} summary={<span className={styles.cardSummary}>{plural(model.pensionPlans.length, 'filing')}</span>}>
                    <RecordList items={model.pensionPlans.map(p => ({
                        more: p.more,
                        title: [p.name, p.year && `plan year ${p.year}`].filter(Boolean).join(' · '),
                        facts: [p.participants != null && `${p.participants} active participants`, p.totalParticipants != null && `${p.totalParticipants} total`].filter(Boolean).join(' · '),
                        chips: [
                            p.einDiffers && { label: 'EIN differs', tone: 'watch' },
                            p.extension && { label: 'Extension', tone: 'neutral' },
                            p.union && { label: 'Union plan', tone: 'neutral' },
                        ].filter(Boolean),
                        details: p.details,
                    }))} />
                </Section>
            )}
            {visible('asLender') && (
                <Section {...sectionProps('asLender')} summary={
                    <span className={styles.cardSummary}>
                        {plural(model.asLender.length, 'active filing')}{model.asLenderEnded.length ? `, ${model.asLenderEnded.length} ended` : ''} where this company is the lender
                    </span>
                }>
                    {model.asLender.length > 0 && <RecordList items={model.asLender.map(u => lenderItem(u, false))} />}
                    {model.asLenderEnded.length > 0 && (
                        <div>
                            <button type='button' className={styles.uccToggle} onClick={() => setShowLenderEnded(v => !v)} aria-expanded={showLenderEnded}>
                                <ExpandMoreIcon className={cx(styles.uccChevron, !showLenderEnded && styles.chevronClosed)} />
                                <span>{plural(model.asLenderEnded.length, 'ended filing')}</span>
                            </button>
                            {showLenderEnded && <RecordList items={model.asLenderEnded.map(u => lenderItem(u, true))} />}
                        </div>
                    )}
                </Section>
            )}

        </div>
    )
}

export default withReportErrorBoundary(BusinessDetailReport, 'report')
